import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_TRUCKX_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableTruckXAppInput = {
  appType: AppTypeEnum
  dotNumber: string
}

const formSchema = z.object({
  dotNumber: z.string().min(1, 'DOT number is required')
})

/**
 * This is the modal for the TruckX app.
 */
export function TruckXModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const workspaceDOTNumber = selectedWorkspace?.workspace.dot || ''
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      dotNumber: workspaceDOTNumber
    },
    resolver: zodResolver(formSchema)
  })

  const [enableTruckXApp, { loading }] = useMutation<
    { enableTruckXApp: { appEnabled: boolean } },
    EnableTruckXAppInput
  >(ENABLE_TRUCKX_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('TruckX app enabled successfully!')
      handleClose()
    },
    onError: (err: Error) => {
      const errorMessage =
        err.message === 'Invalid DOT number or carrier not found'
          ? 'Invalid DOT number. Please verify and try again.'
          : err.message === 'TruckX app could not be setup at this time'
            ? 'TruckX app could not be setup at this time. Please contact support@chaineapp.com.'
            : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleOpenChange = (open: boolean) => {
    if (open) {
      form.reset({
        dotNumber: workspaceDOTNumber
      })
    } else {
      setFormError('')
      form.reset({
        dotNumber: workspaceDOTNumber
      })
      onClose()
    }
  }

  const handleClose = () => {
    setFormError('')
    form.reset({
      dotNumber: workspaceDOTNumber
    })
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableTruckXApp({
      variables: {
        appType: appType as AppTypeEnum,
        dotNumber: values.dotNumber
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable TruckX for Chain</DialogTitle>
          <DialogDescription>
            {workspaceDOTNumber
              ? 'Your workspace DOT number has been pre-filled. You can modify it if needed.'
              : 'Enabling this integration requires your carrier DOT number.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="dotNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>DOT Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your carrier DOT number" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              {!!app?.docs && (
                <a
                  href={app.docs}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                  See detailed steps here
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
